import { ReactNode, useMemo } from 'react';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import useToken, { toStatsigUser } from './common/helper/useToken';

const STATSIG_CLIENT_KEY = process.env.REACT_APP_STATSIG_CLIENT_KEY ?? '';
// Must match the backend's tier (zoom_logi/statsig_client.py) so a gate's
// "Environment Tier" rule resolves the same on both sides.
const STATSIG_TIER = process.env.REACT_APP_STATSIG_TIER || 'development';

interface StatsigAppProviderProps {
  children: ReactNode;
}

/**
 * No loadingComponent is passed to StatsigProvider below, so children render
 * immediately rather than blocking the whole app on Statsig's network init —
 * gates just default to off until the client finishes loading, which is the
 * right failure mode for feature-gating (fail hidden, not fail blocked).
 */
export default function StatsigAppProvider({
  children,
}: StatsigAppProviderProps) {
  // Without a key, a real client would still fire network requests (and fail
  // with 401s, logged as console errors on every page) -- skip constructing
  // one entirely so gates just fall back to the SDK's built-in noop client
  // (useGateValue -> false, no network) wherever the key isn't configured
  // (CI, or any env the secret hasn't been wired into).
  if (!STATSIG_CLIENT_KEY) {
    return <>{children}</>;
  }
  return (
    <StatsigAppProviderWithClient>{children}</StatsigAppProviderWithClient>
  );
}

function StatsigAppProviderWithClient({ children }: StatsigAppProviderProps) {
  const { decodedUserToken } = useToken();
  const decoded = decodedUserToken();

  const statsigUser = toStatsigUser(decoded);

  const plugins = useMemo(
    () => [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
    []
  );

  // useClientAsyncInit only ever constructs the StatsigClient with the user
  // available on the very first render of this component (its internal
  // useMemo has an empty dep array), and this provider sits above the router
  // with nothing forcing it to re-render on login. So the user set here is
  // effectively permanent for the tab -- LoginForm pushes the real user in
  // via client.updateUserAsync() once login succeeds instead.
  const { client } = useClientAsyncInit(STATSIG_CLIENT_KEY, statsigUser, {
    plugins,
    environment: { tier: STATSIG_TIER },
  });

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}
